import { ROLES } from '@app/permissions/roles';
import { getUserRequest } from '@features/users/store';
import { selectIsUserDownloading, selectUserById } from '@features/users/store/selectors';
import { User } from '@features/users/types';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EmailIcon from '@mui/icons-material/Email';
import ErrorIcon from '@mui/icons-material/Error';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import {
    Avatar,
    Box,
    BoxProps,
    IconButton,
    Link,
    Popover,
    PopoverProps,
    Skeleton,
    SxProps,
    Typography,
    TypographyVariant,
} from '@mui/material';
import CopyToClipboardButton from '@shared/components/others/CopyToClipboardButton/CopyToClipboardButton';
import HtmlTooltip from '@shared/components/ui/HtmlTooltip';
import UtmsChip from '@shared/components/ui/UtmsChip';
import { UI_PROFILE_PATH } from '@shared/constants/pathsConstants';
import { convertDateToHumanReadableFormat, toTitleCase } from '@shared/utils/utilsOld';
import { FC, MouseEvent, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

interface PopoverInfo {
    avatar?: boolean;
    rocket?: boolean;
    email?: boolean;
    profileLink?: boolean;
    role?: boolean;
}

interface UserContainerOptionsProps {
    name?: {
        variant: TypographyVariant;
    };
    avatar?: {
        size: number;
    };
}

interface UserProfileProps {
    createdUserId?: number;
    createdDate?: Date | string;
    editedUserId?: number;
    editedDate?: Date | string;
    createdLabel?: string;
    editedLabel?: string;
    showDateTooltip?: boolean;
    showAvatar?: boolean;
    userContainerProps?: {
        sx?: SxProps;
        box?: BoxProps;
        options?: UserContainerOptionsProps;
    };
    popoverInfo?: PopoverInfo;
    popoverProps?: PopoverProps;
    popoverTrigger?: 'hover' | 'click';
}

const dateConvertOptions = {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
};

const UserProfile: FC<UserProfileProps> = memo(
    ({
        createdUserId,
        createdDate,
        editedUserId,
        editedDate,
        createdLabel,
        editedLabel,
        showDateTooltip,
        showAvatar = true,
        userContainerProps,
        popoverInfo,
        popoverProps,
        popoverTrigger = 'click',
    }) => {
        const dispatch = useDispatch();
        const navigate = useNavigate();
        const { t } = useTranslation();
        const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
        const [isAvatarLoading, setIsAvatarLoading] = useState(false);
        const [isAvatarError, setIsAvatarError] = useState(false);
        const open = Boolean(anchorEl);
        const createdUser = useSelector(selectUserById(createdUserId as number));
        const editedUser = useSelector(selectUserById(editedUserId as number));
        const user = useMemo(() => {
            return (editedUserId && editedUser) || (createdUserId && createdUser) || null;
        }, [editedUserId, editedUser, createdUserId, createdUser]);
        const userId = useMemo(() => editedUserId || (createdUserId as number), [editedUserId, createdUserId]);
        const date = (editedUserId && editedDate) || (createdUserId && createdDate);
        const label = (editedUser && editedLabel) || (createdUser && createdLabel);
        const rocketUsername = useMemo(() => {
            if (!user) return '';
            const firstName = user.first_name?.toLowerCase() || '-';
            const lastName = user.last_name?.toLowerCase() || '-';
            return `@${lastName}.${firstName}`;
        }, [user]);
        const isUserLoading = useSelector(useMemo(() => selectIsUserDownloading(userId), [userId]));
        const {
            sx: userContainerPropsSx,
            options: userContainerPropsOptions,
            box: userContainerPropsBox,
            ...otherUserContainerProps
        } = userContainerProps || {};

        const handlePopoverToggle = useCallback((e: MouseEvent<HTMLElement>) => {
            e.stopPropagation();
            const target = e.currentTarget;
            setAnchorEl((prev) => (prev ? null : target));
        }, []);

        const handlePopoverClose = useCallback(() => {
            setAnchorEl(null);
        }, []);

        const handleMouseEnter = useCallback((e: MouseEvent<HTMLElement>) => {
            e.stopPropagation();
            setAnchorEl(e.currentTarget);
        }, []);

        const handleMouseLeave = handlePopoverClose;

        const handleNavigateToUser = useCallback(
            (user: User | null) => (_e: MouseEvent<HTMLButtonElement>) => {
                user && navigate(`/${UI_PROFILE_PATH}/${user.pk}`);
            },
            [navigate],
        );

        useEffect(() => {
            if (!user?.avatar) {
                setIsAvatarLoading(false);
                setIsAvatarError(true);
                return;
            }

            const img = new Image();

            img.onload = () => {
                setIsAvatarLoading(false);
                setIsAvatarError(false);
            };
            img.onerror = () => {
                setIsAvatarLoading(false);
                setIsAvatarError(true);
            };

            setIsAvatarLoading(true);
            img.src = user?.avatar;
        }, [user?.avatar]);

        useEffect(() => {
            function getCreatedAndEditedUser(createdUserId: number, editedUserId: number) {
                if (createdUserId) {
                    getCreatedUser(createdUserId);
                }
                if (editedUserId) {
                    getEditedUser(editedUserId);
                }
            }

            function getEditedUser(editedUserId: number) {
                if (editedUserId) {
                    dispatch(getUserRequest(editedUserId));
                }
            }

            function getCreatedUser(createdUserId: number) {
                if (createdUserId && !editedUserId) {
                    dispatch(getUserRequest(createdUserId));
                }
            }

            if (createdUserId && editedUserId) {
                getCreatedAndEditedUser(createdUserId, editedUserId);
            } else if (createdUserId) {
                getCreatedUser(createdUserId);
            } else if (editedUserId) {
                getEditedUser(editedUserId);
            }
        }, [dispatch, createdUserId, editedUserId]);

        const renderAvatar = useMemo(() => {
            if (isUserLoading || isAvatarLoading) {
                return <Skeleton variant="circular" sx={{ width: '100%', height: '100%' }} />;
            }

            return (
                <Avatar
                    src={isAvatarError ? undefined : user?.avatar}
                    sx={{ width: '100%', height: '100%', objectFit: 'cover', flexShrink: '0' }}
                />
            );
        }, [isUserLoading, isAvatarLoading, isAvatarError, user?.avatar]);

        const renderUserInfo = useMemo(() => {
            if (isUserLoading) {
                return (
                    <>
                        <Skeleton sx={{ width: '120px', height: '20px' }} />
                        {popoverInfo?.profileLink && <Skeleton variant="circular" sx={{ width: 16, height: 16 }} />}
                    </>
                );
            }

            return (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '8px',
                    }}
                >
                    <Typography
                        sx={{
                            overflowWrap: 'break-word',
                            maxWidth: '120px',
                            width: 'auto',
                            flexShrink: 0,
                            fontSize: 16,
                            marginTop: '1px',
                            cursor: popoverInfo?.profileLink ? 'pointer' : 'default',
                        }}
                        onClick={popoverInfo?.profileLink ? handleNavigateToUser(user) : undefined}
                    >
                        {user ? user.first_name : '-'} {user ? user.last_name : '-'}
                    </Typography>
                    {popoverInfo?.profileLink && (
                        <HtmlTooltip title={t('go_to_users_profile')} placement="bottom">
                            <IconButton size="small" onClick={handleNavigateToUser(user)}>
                                <OpenInNewIcon />
                            </IconButton>
                        </HtmlTooltip>
                    )}
                </Box>
            );
        }, [isUserLoading, user, popoverInfo, handleNavigateToUser, t]);

        const renderEmailInfo = useMemo(() => {
            if (popoverInfo?.email) {
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <EmailIcon fontSize="small" />
                        {isUserLoading ? (
                            <Skeleton variant="text" sx={{ width: '130px', height: '20px' }} />
                        ) : (
                            <Link
                                style={{
                                    display: 'block',
                                    textAlign: 'center',
                                }}
                                href={`mailto: ${user?.email}`}
                            >
                                {user?.email}
                            </Link>
                        )}
                    </Box>
                );
            }
            return null;
        }, [popoverInfo, isUserLoading, user?.email]);

        const renderRocketInfo = useMemo(() => {
            if (popoverInfo?.rocket && user?.role !== ROLES.STUDENT) {
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <RocketLaunchIcon
                            fontSize="small"
                            sx={{
                                color: '#F3425BFF',
                            }}
                        />
                        <Box sx={{ display: 'flex', alignItems: 'center', userSelect: 'text', fontWeight: '600' }}>
                            {isUserLoading ? (
                                <Skeleton variant="text" sx={{ width: '130px', height: '20px' }} />
                            ) : (
                                <HtmlTooltip title={t('click_to_copy')} placement="right">
                                    <CopyToClipboardButton textToCopy={rocketUsername}>
                                        {rocketUsername}
                                    </CopyToClipboardButton>
                                </HtmlTooltip>
                            )}
                        </Box>
                    </Box>
                );
            }
            return null;
        }, [popoverInfo?.rocket, user?.role, isUserLoading, rocketUsername, t]);

        const hasPopoverInfo = popoverInfo && Object.keys(popoverInfo).length > 0;
        const containerHandlers = hasPopoverInfo
            ? {
                  onClick: popoverTrigger === 'click' ? handlePopoverToggle : undefined,
                  onMouseEnter: popoverTrigger === 'hover' ? handleMouseEnter : undefined,
                  onMouseLeave: popoverTrigger === 'hover' ? handleMouseLeave : undefined,
              }
            : {};

        return (
            <Box
                aria-owns={open ? 'mouse-over-popover' : undefined}
                aria-haspopup="true"
                {...containerHandlers}
                sx={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    ...userContainerPropsSx,
                }}
                {...(otherUserContainerProps as BoxProps)}
            >
                {isUserLoading && (
                    <Box sx={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                        {showAvatar && (
                            <Skeleton
                                variant="circular"
                                sx={{
                                    width: userContainerPropsOptions?.avatar?.size || 25,
                                    height: userContainerPropsOptions?.avatar?.size || 25,
                                }}
                            />
                        )}
                        {userId && <Skeleton variant="text" sx={{ width: '100px' }} />}
                    </Box>
                )}
                {!isUserLoading && user && (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '4px',
                        }}
                    >
                        {label && (
                            <Typography
                                variant="body2"
                                sx={{
                                    whiteSpace: 'nowrap',
                                }}
                                color="textSecondary"
                            >
                                {label}
                            </Typography>
                        )}
                        <Box
                            sx={{
                                cursor: hasPopoverInfo ? 'pointer' : 'default',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                            }}
                        >
                            {showAvatar && (
                                <Box
                                    sx={{
                                        flexShrink: 0,
                                        width: `${userContainerPropsOptions?.avatar?.size || 25}px`,
                                        height: `${userContainerPropsOptions?.avatar?.size || 25}px`,
                                    }}
                                >
                                    {renderAvatar}
                                </Box>
                            )}
                            <Typography
                                variant={userContainerPropsOptions?.name?.variant || 'body1'}
                                style={{ whiteSpace: 'nowrap' }}
                            >
                                {`${user['first_name']} ${user['last_name']}`}
                                {hasPopoverInfo && (
                                    <Popover
                                        id="mouse-over-popover"
                                        open={open}
                                        anchorEl={anchorEl}
                                        anchorOrigin={
                                            popoverProps?.anchorOrigin || { vertical: 'bottom', horizontal: 'left' }
                                        }
                                        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                        onClose={handlePopoverClose}
                                        onClick={(e) => e.stopPropagation()}
                                        hideBackdrop={popoverTrigger === 'hover'}
                                        slotProps={{
                                            paper: {
                                                sx: {
                                                    ...popoverProps?.sx,
                                                    maxWidth: '1000px',
                                                    minWidth: '250px',
                                                },
                                            },
                                            root: {
                                                sx: {
                                                    width: '0',
                                                    maxWidth: '0',
                                                },
                                            },
                                        }}
                                    >
                                        <Box sx={{ padding: '12px 20px' }}>
                                            <Box sx={{ display: 'flex', gap: '12px' }}>
                                                {popoverInfo?.avatar && (
                                                    <Box sx={{ flexShrink: '0', width: '60px', height: '60px' }}>
                                                        {renderAvatar}
                                                    </Box>
                                                )}
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        gap: '6px',
                                                    }}
                                                >
                                                    {renderUserInfo}
                                                    {popoverInfo?.role && user?.role && (
                                                        <Box sx={{ marginBottom: '12px' }}>
                                                            <UtmsChip
                                                                sx={{
                                                                    fontSize: '0.8125rem',
                                                                    flexShrink: 0,
                                                                }}
                                                                label={user?.role}
                                                            />
                                                        </Box>
                                                    )}
                                                </Box>
                                            </Box>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                {renderEmailInfo}
                                                {renderRocketInfo}
                                            </Box>
                                        </Box>
                                    </Popover>
                                )}
                            </Typography>
                        </Box>
                        {date &&
                            (!showDateTooltip ? (
                                <Typography
                                    sx={{
                                        fontSize: '0.8rem',
                                    }}
                                >
                                    {toTitleCase(convertDateToHumanReadableFormat(date, dateConvertOptions))}
                                </Typography>
                            ) : (
                                <HtmlTooltip title={convertDateToHumanReadableFormat(date, dateConvertOptions)}>
                                    <AccessTimeIcon
                                        fontSize="small"
                                        sx={{ marginRight: '5px', cursor: 'pointer', color: 'rgba(0, 0, 0, 0.54);' }}
                                    />
                                </HtmlTooltip>
                            ))}
                    </Box>
                )}
                {!isUserLoading && !user && (
                    <Box sx={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                        <ErrorIcon
                            sx={{
                                width: userContainerPropsOptions?.avatar?.size || 25,
                                height: userContainerPropsOptions?.avatar?.size || 25,
                            }}
                            color="error"
                        />
                        <Typography color={'var(--text-secondary)'}>{t('failed_to_fetch_user')}...</Typography>
                    </Box>
                )}
            </Box>
        );
    },
);

export default UserProfile;
